import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Target, Users, Zap, Clock, ListChecks, Play } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { calculateScoresClient } from "@/utils/scoring";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_DIMENSIONS = [
  "capability", "capability", "capability", "capability", "capability",
  "collaboration", "collaboration", "collaboration", "collaboration", "collaboration",
  "energy", "energy", "energy", "energy", "energy"
];

const dimensionConfig = {
  capability: { icon: Target, color: "blue" },
  collaboration: { icon: Users, color: "purple" },
  energy: { icon: Zap, color: "orange" },
};

export default function QuestionnairePage() {
  const { eventCode } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [event, setEvent] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(!!eventCode);
  const [submitting, setSubmitting] = useState(false);

  const isOpenMode = !eventCode;

  const questions = t('questions') || [];

  useEffect(() => {
    if (!eventCode) return;
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/code/${eventCode}`);
        setEvent(response.data);
      } catch (error) {
        toast.error("Event not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventCode, navigate]);

  const questionText = questions[currentQuestion] || "";
  const dimension = QUESTION_DIMENSIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / 15) * 100;
  const config = dimensionConfig[dimension];
  const DimensionIcon = config?.icon || Target;

  const handleAnswer = (score) => {
    const questionId = currentQuestion + 1;
    setAnswers({ ...answers, [questionId]: score });
    
    // Auto-advance after short delay
    setTimeout(() => {
      if (currentQuestion < 14) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < 15) {
      toast.error("Please answer all questions");
      return;
    }

    setSubmitting(true);
    try {
      if (isOpenMode) {
        // Open mode: calculate scores client-side, don't store in DB
        const results = calculateScoresClient(answers);
        sessionStorage.setItem("teampower_results", JSON.stringify(results));
        sessionStorage.setItem("teampower_event", JSON.stringify({ name: "Quick Assessment", code: "" }));
        sessionStorage.setItem("teampower_open_mode", "true");
        navigate("/results");
      } else {
        // Event mode: submit to server
        const response = await axios.post(`${API}/responses`, {
          event_code: eventCode,
          answers: answers,
        });
        sessionStorage.setItem("teampower_results", JSON.stringify(response.data));
        sessionStorage.setItem("teampower_event", JSON.stringify(event));
        sessionStorage.removeItem("teampower_open_mode");
        navigate("/results");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Intro Screen
  if (showIntro) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
        {/* Header */}
        <header className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-pink-600 flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-sm tracking-wide text-gray-900">TEAMPOWER</span>
            </div>
            <LanguageSwitcher />
          </div>
        </header>

        {/* Intro Content */}
        <main className="flex-1 flex flex-col items-center px-6 py-12">
          {/* Open Mode: Three Dimensions Section */}
          {isOpenMode && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-4xl mb-10"
            >
              <h2 className="font-heading text-2xl md:text-3xl text-center mb-8 uppercase tracking-wide text-gray-900">
                {t('dimensions.threeDimensions')} <span className="text-pink-600">{t('dimensions.teamPower')}</span>
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-heading text-xl mb-2 text-blue-600">{t('dimensions.capabilityUpper').toUpperCase()}</h3>
                  <p className="text-gray-600 text-sm">{t('dimensions.capabilityDesc')}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-purple-400 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-heading text-xl mb-2 text-purple-600">{t('dimensions.collaborationUpper').toUpperCase()}</h3>
                  <p className="text-gray-600 text-sm">{t('dimensions.collaborationDesc')}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-pink-400 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-pink-600" />
                  </div>
                  <h3 className="font-heading text-xl mb-2 text-pink-600">{t('dimensions.energyUpper').toUpperCase()}</h3>
                  <p className="text-gray-600 text-sm">{t('dimensions.energyDesc')}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Assessment Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: isOpenMode ? 0.2 : 0 }}
            className="w-full max-w-2xl"
          >
            <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
              {/* Event Info */}
              <div className="mb-8">
                <h1 className="font-heading text-3xl md:text-4xl tracking-wide mb-4 text-gray-900">
                  {isOpenMode ? (language === 'el' ? 'ΓΡΗΓΟΡΗ ΑΞΙΟΛΟΓΗΣΗ' : 'QUICK ASSESSMENT') : event?.name}
                </h1>
                {!isOpenMode && event?.location && (
                  <p className="text-gray-500">{event.location}</p>
                )}
                {isOpenMode ? (
                  <p className="text-gray-500 text-sm mt-2">
                    {language === 'el' ? 'Ανακαλύψτε τις δυνάμεις και τις ανάγκες ανάπτυξης της ομάδας σας' : 'Discover your team\'s strengths and development needs'}
                  </p>
                ) : (
                  <p className="text-gray-400 text-sm mt-2">{event?.date}</p>
                )}
              </div>

              {/* Divider */}
              <div className="w-16 h-1 bg-pink-500 mx-auto mb-8 rounded-full" />

              {/* Assessment Info */}
              <h2 className="font-heading text-xl mb-6 text-pink-600">
                {language === 'el' ? 'ΑΞΙΟΛΟΓΗΣΗ ΔΥΝΑΜΗΣ ΟΜΑΔΑΣ' : 'TEAM POWER ASSESSMENT'}
              </h2>

              {/* Dimensions - only show for event mode (open mode shows them above) */}
              {!isOpenMode && (
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-2">
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm text-blue-600">{t('dimensions.capability')}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-2">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <p className="text-sm text-purple-600">{t('dimensions.collaboration')}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mx-auto mb-2">
                      <Zap className="w-6 h-6 text-pink-600" />
                    </div>
                    <p className="text-sm text-pink-600">{t('dimensions.energy')}</p>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-gray-100 rounded-xl p-6 mb-8 text-left">
                <div className="flex items-center gap-3 mb-4">
                  <ListChecks className="w-5 h-5 text-pink-600" />
                  <span className="font-medium text-gray-900">
                    {language === 'el' ? 'ΟΔΗΓΙΕΣ' : 'INSTRUCTIONS'}
                  </span>
                </div>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li>• {language === 'el' ? '15 ερωτήσεις σχετικά με την ομάδα σας' : '15 questions about your team'}</li>
                  <li>• {language === 'el' ? 'Βαθμολογήστε από 1 (Διαφωνώ Απόλυτα) έως 5 (Συμφωνώ Απόλυτα)' : 'Rate from 1 (Strongly Disagree) to 5 (Strongly Agree)'}</li>
                  <li>• {language === 'el' ? 'Οι απαντήσεις σας είναι ανώνυμες' : 'Your responses are anonymous'}</li>
                  <li>• {language === 'el' ? 'Θα λάβετε τα προσωπικά σας αποτελέσματα αμέσως' : 'You will receive your personal results immediately'}</li>
                </ul>
              </div>

              {/* Estimated Time - inside card */}
              <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-6">
                <Clock className="w-4 h-4" />
                <span>{language === 'el' ? 'Εκτιμώμενος χρόνος: 1-1.5 λεπτά' : 'Estimated time: 1-1.5 minutes'}</span>
              </div>

              {/* Start Button */}
              <Button
                onClick={() => setShowIntro(false)}
                className="bg-pink-600 hover:bg-pink-700 text-white font-bold px-12 py-6 text-lg shadow-lg uppercase"
                data-testid="start-questionnaire-button"
              >
                <Play className="w-5 h-5 mr-2" />
                {language === 'el' ? 'Εναρξη' : 'Start'}
              </Button>
            </div>
          </motion.div>
        </main>

        {/* Footer */}
        <footer className="px-6 py-4 border-t border-gray-200 bg-white">
          <div className="max-w-4xl mx-auto flex flex-col items-center gap-3">
            <img 
              src="/summit-logo.png" 
              alt="Summit Logo" 
              className="h-8 object-contain"
            />
            <p className="text-gray-500 text-sm">
              Powered by Bitou & Summit | Members of International Team Experience Network
            </p>
          </div>
        </footer>
      </div>
    );
  }

  const isLastQuestion = currentQuestion === 14;
  const allAnswered = Object.keys(answers).length === 15;
  const currentQuestionId = currentQuestion + 1;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-pink-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-heading text-sm tracking-wide text-gray-900">TEAMPOWER CHECK</p>
              <p className="text-sm text-gray-400">{event?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <p className="text-2xl font-bold text-pink-600">{currentQuestion + 1}<span className="text-gray-400">/15</span></p>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="px-6 py-3 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <Progress value={progress} className="h-2 bg-gray-200" />
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-sm"
              data-testid="question-card"
            >
              {/* Dimension Badge */}
              <div className="flex items-center gap-2 mb-6">
                <div className={`w-10 h-10 rounded-lg ${dimension === 'capability' ? 'bg-blue-100' : dimension === 'collaboration' ? 'bg-purple-100' : 'bg-pink-100'} flex items-center justify-center`}>
                  <DimensionIcon className={`w-5 h-5 ${dimension === 'capability' ? 'text-blue-600' : dimension === 'collaboration' ? 'text-purple-600' : 'text-pink-600'}`} />
                </div>
                <span className={`font-heading text-sm tracking-wide uppercase ${dimension === 'capability' ? 'text-blue-600' : dimension === 'collaboration' ? 'text-purple-600' : 'text-pink-600'}`}>
                  {t(`dimensions.${dimension}Upper`)}
                </span>
              </div>

              {/* Question */}
              <h2 className="text-xl md:text-2xl font-normal mb-10 leading-relaxed text-gray-800 normal-case" data-testid="question-text">
                {questionText}
              </h2>

              {/* Scale Labels */}
              <div className="flex justify-between text-sm text-gray-500 mb-3 px-1">
                <span>{t('questionnaire.stronglyDisagree')}</span>
                <span>{t('questionnaire.stronglyAgree')}</span>
              </div>

              {/* Answer Options */}
              <div className="flex justify-center gap-3 md:gap-4" data-testid="answer-options">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    onClick={() => handleAnswer(score)}
                    className={`option-btn ${answers[currentQuestionId] === score ? 'selected' : ''}`}
                    data-testid={`option-${score}`}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentQuestion === 0}
              className="text-gray-500 hover:text-gray-900"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('questionnaire.back')}
            </Button>

            {isLastQuestion && allAnswered ? (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-pink-600 hover:bg-pink-700 text-white font-bold px-8 shadow-lg"
                data-testid="submit-button"
              >
                {submitting ? t('questionnaire.submitting') : t('questionnaire.submit')}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={!answers[currentQuestionId] || isLastQuestion}
                className="text-gray-500 hover:text-gray-900"
                data-testid="next-button"
              >
                {t('questionnaire.next')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-3 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex justify-between items-center text-sm text-gray-500">
          <span>Powered by Bitou & Summit | Members of International Team Experience Network</span>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{language === 'el' ? 'Εκτιμώμενος χρόνος: 1-1.5 λεπτά' : 'Estimated time: 1-1.5 minutes'}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
